/**
 * Utility for handling sign-up pre-fill data from URL parameters.
 *
 * Consumes URL parameters (institution_name, firstname, lastname, udtype, email),
 * removes them from the URL, and stores them in localStorage.
 */

import { UserDetailedType, UserInfo } from "src/views/Onboarding/OnboardingContainer";

const STORAGE_KEY = "signup_prefill_data";

export interface SignupPrefillData {
  institutionName?: string;
  firstName?: string;
  lastName?: string;
  udtype?: string;
  email?: string;
}

/**
 * Maps udtype URL param values to UserDetailedType and role for onboarding.
 */
export const mapUdtypeToUserInfo = (udtype: string | undefined): Partial<UserInfo> => {
  if (!udtype) return {};

  const normalizedUdtype = udtype.toLowerCase().trim();

  const udtypeMap: Record<string, { detailedType: UserDetailedType; role: UserInfo["role"] }> = {
    "company sponsor": {
      detailedType: UserDetailedType.COMPANY_SPONSOR,
      role: "company-sponsor",
    },
    "company-sponsor": {
      detailedType: UserDetailedType.COMPANY_SPONSOR,
      role: "company-sponsor",
    },
    "university scientist": {
      detailedType: UserDetailedType.UNIVERSITY_SCIENTIST,
      role: "research-scientist",
    },
    "university-scientist": {
      detailedType: UserDetailedType.UNIVERSITY_SCIENTIST,
      role: "research-scientist",
    },
    "startup scientist": {
      detailedType: UserDetailedType.STARTUP_SCIENTIST,
      role: "company-scientist",
    },
    "startup-scientist": {
      detailedType: UserDetailedType.STARTUP_SCIENTIST,
      role: "company-scientist",
    },
    supplier: {
      detailedType: UserDetailedType.SUPPLIER,
      role: "company-scientist",
    },
    consultant: {
      detailedType: UserDetailedType.CONSULTANT,
      role: "company-scientist",
    },
    "university administrator": {
      detailedType: UserDetailedType.UNIVERSITY_ADMINISTRATOR,
      role: "research-administrator",
    },
    "university-administrator": {
      detailedType: UserDetailedType.UNIVERSITY_ADMINISTRATOR,
      role: "research-administrator",
    },
  };

  return udtypeMap[normalizedUdtype] ?? {};
};

const URL_PARAM_KEYS = {
  institution_name: "institutionName",
  firstname: "firstName",
  lastname: "lastName",
  udtype: "udtype",
  email: "email",
} as const;

/**
 * Reads URL parameters, removes them from the URL, and stores them in localStorage.
 * Should be called once on app initialization.
 */
export const consumeSignupPrefillParams = (): SignupPrefillData => {
  const searchParams = new URLSearchParams(window.location.search);
  const prefillData: SignupPrefillData = {};
  let hasParams = false;

  // Extract params from URL
  for (const [urlKey, storageKey] of Object.entries(URL_PARAM_KEYS)) {
    const value = searchParams.get(urlKey);
    if (value) {
      prefillData[storageKey] = value;
      searchParams.delete(urlKey);
      hasParams = true;
    }
  }

  if (hasParams) {
    // Merge with existing stored data (new params take precedence)
    const existingData = getSignupPrefillData();
    const mergedData = { ...existingData, ...prefillData };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedData));

    // Update URL without the consumed params
    const newUrl =
      searchParams.toString().length > 0
        ? `${window.location.pathname}?${searchParams.toString()}`
        : window.location.pathname;

    window.history.replaceState({}, "", newUrl);

    return mergedData;
  }

  return getSignupPrefillData();
};

/**
 * Gets the pre-fill data from localStorage.
 * @param shouldClear - If true, clears the data from localStorage after reading. Defaults to false.
 */
export const getSignupPrefillData = (shouldClear = false): SignupPrefillData => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    const data = JSON.parse(stored);
    if (shouldClear) {
      localStorage.removeItem(STORAGE_KEY);
    }
    return data;
  } catch {
    return {};
  }
};

/**
 * Clears all pre-fill data from localStorage.
 */
export const clearSignupPrefillData = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Gets and clears a specific field from the pre-fill data.
 * Useful for one-time consumption of individual fields.
 */
export const popSignupPrefillField = <K extends keyof SignupPrefillData>(
  field: K
): SignupPrefillData[K] | undefined => {
  const data = getSignupPrefillData();
  const value = data[field];

  if (value !== undefined) {
    delete data[field];
    if (Object.keys(data).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  return value;
};
